import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";

export interface AttendanceRow {
  id: string;
  userId: string;
  userFullName: string | null;
  userEmail: string | null;
  userRole: string | null;
  userEmpId: string | null;
  calendarDate: string;
  markedInAt: string | null;
  markedInLat: string | null;
  markedInLng: string | null;
  markedInWithinGeofence: boolean;
  markedInDistanceM: number | null;
  markedInAccuracyM: number | null;
  markedInGeofenceId: string | null;
  markedInGeofenceName: string | null;
  markedInOverrideNote: string;
  markedOutAt: string | null;
  markedOutLat: string | null;
  markedOutLng: string | null;
  markedOutWithinGeofence: boolean | null;
  markedOutDistanceM: number | null;
  markedOutAccuracyM: number | null;
  markedOutGeofenceId: string | null;
  markedOutGeofenceName: string | null;
  markedOutOverrideNote: string;
  isHalfDay: boolean;
  markInRegularised: boolean;
  markOutRegularised: boolean;
}

const todayAttendanceKey = ["attendance", "today"] as const;
const meStateKey = ["me", "session-state"] as const;
const listKey = (params: object) => ["attendance", "list", params] as const;

export interface AttendanceListResponse {
  results: AttendanceRow[];
  nextCursor: string | null;
  prevCursor: string | null;
}

export function useAttendanceList(params: {
  userId?: string;
  from?: string;
  to?: string;
  mismatchOnly?: boolean;
} = {}, enabled = true) {
  return useQuery<AttendanceListResponse>({
    queryKey: listKey(params),
    queryFn: () =>
      api
        .get<AttendanceListResponse>("/attendance", {
          params: {
            userId: params.userId || undefined,
            from: params.from || undefined,
            to: params.to || undefined,
            mismatchOnly: params.mismatchOnly ? "true" : undefined,
          },
        })
        .then((r) => r.data),
    enabled,
  });
}

export interface AttendanceTodayState {
  attendance: AttendanceRow | null;
  onLeaveToday: boolean;
  hasAssignedLocations: boolean;
}

export function useTodayAttendance(enabled = true) {
  return useQuery<AttendanceTodayState>({
    queryKey: todayAttendanceKey,
    queryFn: () =>
      api.get<AttendanceTodayState>("/attendance/today").then((r) => r.data),
    enabled,
  });
}

export function useMarkIn() {
  const qc = useQueryClient();
  return useMutation<AttendanceRow, unknown, { lat: number; lng: number; accuracyM?: number; overrideNote?: string; locationId?: string }>({
    mutationFn: (input) => api.post<AttendanceRow>("/attendance/mark-in", input).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: meStateKey });
      qc.invalidateQueries({ queryKey: todayAttendanceKey });
    },
  });
}

export function useMarkOut() {
  const qc = useQueryClient();
  return useMutation<AttendanceRow, unknown, { lat: number; lng: number; accuracyM?: number; overrideNote?: string; locationId?: string }>({
    mutationFn: (input) => api.post<AttendanceRow>("/attendance/mark-out", input).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: meStateKey });
      qc.invalidateQueries({ queryKey: todayAttendanceKey });
    },
  });
}
